import React, { ChangeEvent, useState } from 'react';
import {
  Modal,
  ModalFooter,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ClipboardTextBox,
  VStack,
  Divider,
  Select,
  Text,
  Box,
  Button,
  Spacer,
  Icon,
  Link,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import { isStaging } from '../../../../common/src/utils/globalVariables';
import i18n from '../../../../../i18n';
import LoadingScreen from '../../../../common/src/components/LoadingScreen';
import { Link as RRLink, useLocation } from 'react-router-dom';
import useCompanyScanFlowId from '../../../../company-scan/hooks/useCompanyScanFlowId';
import useIsNLplan from '../../../../common/src/hooks/useIsNLPlan';
import { useParty } from '../../../../parties/src/features/detail/api/getParty';
import { stringifyUrl } from 'query-string';
import { useDistributionSettings } from '../../../../settings/src/features/connections/api/getDistributionSettings';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  partyId: string;
  partyLang: string;
  leftElement?: React.ReactElement;
}

const ShareCompanyScanConversationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  partyId,
  partyLang,
  leftElement,
}) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { isNL } = useIsNLplan();
  const { user } = useAuth();
  const { data: distributionSettings, isLoading: isLoadingDistriSettings } =
    useDistributionSettings();
  const [selectedLang, setSelectedLang] = useState<string>(
    partyLang || i18n.language,
  );
  const flowId = useCompanyScanFlowId();
  const { data: party, isLoading } = useParty(partyId);
  const { trackEvent } = useTracking();

  const handleLangChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLang(e.currentTarget.value);
  };

  const handleCopy = () => {
    trackEvent('Share Campaignlink in Campaigns');
    trackEvent(
      'Copied the share conversation link at the end of the company scan',
    );
  };

  const baseUrl = isStaging
    ? 'conversations.staging.wegroup'
    : 'conversations.wegroup';

  const shareURL = `https://${baseUrl}.${
    isNL ? 'nl' : 'be'
  }?party_id=${partyId}&broker_id=${user?.id}&lang=${selectedLang}`;

  const partyEmail = party?.email || party?.contact_person?.email;

  const mailtoUrl = stringifyUrl(
    {
      url: `mailto:${partyEmail || ''}`,
      query: {
        subject: t('COMPANY-SCAN_SEND-CONVO_URL_MAIL-SUBJECT'),
        body: t('COMPANY-SCAN_SEND-CONVO_URL_MAIL-BODY', {
          url: shareURL,
          broker: user?.name,
        }),
        bcc: distributionSettings?.bcc_on_send_ask_later,
      },
    },
    { skipNull: true, skipEmptyString: true },
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('COMPANY-SCAN_RECOMMENDATIONS_SEND_TITLE')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading || isLoadingDistriSettings ? (
              <LoadingScreen />
            ) : (
              <>
                <VStack align="stretch" color="gray.500">
                  <Text>{t('COMPANY-SCAN_SEND-CONVO_URL_MODAL_DESC')}</Text>
                  <Text>{t('COMPANY-SCAN_SEND-CONVO_URL_MODAL_DESC_2')}</Text>
                </VStack>
                <Divider color="gray.50" my="6" />
                <VStack align="stretch" spacing="4">
                  {/* LANGUAGE SELECT */}
                  <Box>
                    <Text color="text.gray" mb="2">
                      {t('Language')}
                    </Text>
                    <Select
                      maxW="56"
                      onChange={handleLangChange}
                      defaultValue={selectedLang}
                    >
                      <option value="en">{t('English')}</option>
                      <option value="nl">{t('Dutch')}</option>
                      {!isNL && <option value="fr">{t('French')}</option>}
                    </Select>
                  </Box>
                  {/* CLIPBOARD */}
                  <Box>
                    <Text color="text.gray" mb="2">
                      {t('COMPANY-SCAN_SEND-CONVO_URL_LABEL')}
                    </Text>
                    <ClipboardTextBox onCopy={handleCopy} wrap>
                      {shareURL}
                    </ClipboardTextBox>
                  </Box>
                </VStack>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              as={Link}
              href={mailtoUrl}
              alignSelf="flex-start"
              variant="secondary"
              leftIcon={<Icon name="GeneralActionsSendEmail" />}
              onClick={onClose}
              textDecoration="unset"
            >
              {t('COMPANY-SCAN_SEND-CONVO_URL_MAIL-ACTION')}
            </Button>
            <Spacer />
            {leftElement}
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('CLOSE')}
            </Button>
            <Button
              as={RRLink}
              variant="white"
              to={`/conversation/print/${flowId}?partyId=${partyId}&is_ask_later=1&backRef=${
                pathname + search
              }`}
              leftIcon={<Icon name="GeneralActionsPrint" />}
              onClick={() => trackEvent('Print conversation for ask later')}
            >
              {t('DASHBOARD_CONVERSATION_BUTTON_PRINT-CONVERSATION')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ShareCompanyScanConversationModal;
